import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import { useSeo } from '@/hooks/useSeo';

const values = [
  { icon: 'ri-eye-2-line', t: '看得快', d: '把一天的信息压缩成几分钟，点开即读，不用在信息流里大海捞针。' },
  { icon: 'ri-shield-check-line', t: '立得稳', d: '多方口径并陈、进展及时标注，宁可慢半拍，也不抢一个未经核实的瓜。' },
  { icon: 'ri-chat-smile-3-line', t: '说人话', d: '不卖弄词藻、不制造焦虑，用轻松直白的方式讲清一件事的来龙去脉。' },
  { icon: 'ri-refresh-line', t: '跟得紧', d: '大事有追踪、反转有更新，让每一个关注此事的读者都能接上进度。' },
];

const principles = [
  { n: '01', t: '来源为先', d: '优先采用官方口径、当事人声明与可核实的公开信息；二手转述会在文中说明，绝不冒充一手信源。' },
  { n: '02', t: '疑者存疑', d: '信息不全时如实标注"待证实"，宁可不猜，也不做无依据的定论与情绪化诱导。' },
  { n: '03', t: '纠正有担当', d: '一旦发现事实有出入，第一时间修正并在文末补充更正说明，接受读者监督。' },
  { n: '04', t: '不靠制造对立', d: '我们记录的是话题与进展，不靠挑动对立和贩卖焦虑来换取流量。' },
];

const quotes = [
  { name: '追剧小A', role: '每天通勤读者', text: '早上地铁刷一遍合集，当天同事聊的热点基本都能接上话，已经很习惯这种快节奏吃瓜了。' },
  { name: '夜猫子Kiki', role: '热衷吃瓜的夜班族', text: '最喜欢它有进展就更新这一点，追一个反转瓜的时候不用自己在各个平台来回翻，省心很多。' },
  { name: '阿辽', role: '周末才刷资讯的路人', text: '不标题党、不逼我看广告，排版清爽，当作睡前读物也很合适。' },
];

export default function About() {
  useSeo({
    title: '关于我们 | 吃瓜日报',
    description:
      '吃瓜日报是一个专注每日瓜料与热门事件速览的资讯整理站点。认识我们的定位、发展理念与内容原则，也欢迎投稿爆料或合作联系。',
    keywords: '吃瓜日报,关于我们,内容原则,投稿爆料,联系方式',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于吃瓜日报',
        description: '吃瓜日报的定位、理念、内容原则与联系方式介绍。',
        isPartOf: { '@type': 'WebSite', name: '吃瓜日报', url: 'https://chiguadaily.example' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '吃瓜日报',
        url: 'https://chiguadaily.example',
        email: 'hello@chiguadaily.example',
        slogan: '每日合集，速览今日最新瓜料与热门事件',
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <section className="w-full bg-primary-600 text-background-50">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="text-xs text-primary-100" aria-label="面包屑">
              <Link to="/" className="hover:text-background-50">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-background-50">关于我们</span>
            </nav>
            <div className="mt-6 max-w-3xl">
              <p className="text-xs font-black tracking-[0.2em] text-primary-100">ABOUT US</p>
              <h1 className="mt-2 font-heading text-3xl font-black text-background-50 md:text-4xl">
                把纷杂的信息，整理成看得懂的热点合集
              </h1>
              <p className="mt-4 text-sm leading-7 text-primary-100">
                吃瓜日报是一个专注「每日瓜料与热门事件速览」的资讯整理站点。我们不生产未经核实的信息，而是把当天散落在各处的热点，按主题整理成一份能快速读完的日报。
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="flex items-center gap-2 text-xs font-black tracking-[0.2em] text-accent-600">
                <span className="inline-block h-px w-6 bg-accent-500"></span>WHO WE ARE
              </p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-3xl">
                我们是谁，为何而做
              </h2>
              <p className="mt-4 text-[15px] leading-8 text-foreground-700">
                编辑部由几位长期活跃在娱乐、科技与消费圈的内容作者组成。过去我们也经历过"热搜刚点开就发现是旧瓜""一条新闻要翻三个平台才能拼齐"的抓狂，于是决定自己动手，把这些碎片整理成日报。
              </p>
              <p className="mt-4 text-[15px] leading-8 text-foreground-700">
                我们的定位很朴素：不做夸张标题、不追着制造焦虑，只做读者愿意每天点开的"今日瓜料速览"。快，但不失谨慎；轻松，但不放弃核对。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {values.map((v) => (
                <div key={v.t} className="rounded-xl border border-background-200 bg-background-100 p-5">
                  <span className="grid h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-lg text-background-50">
                    <i className={v.icon}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-900">{v.t}</h3>
                  <p className="mt-1.5 text-sm leading-6 text-foreground-600">{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <section id="principles" className="w-full bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 text-xs font-black tracking-[0.2em] text-accent-600">
                  <span className="inline-block h-px w-6 bg-accent-500"></span>OUR PRINCIPLES
                </p>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-3xl">我们的内容原则</h2>
              </div>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {principles.map((p) => (
                <div key={p.n} className="rounded-xl border border-background-200 bg-background-50 p-6">
                  <span className="font-heading text-3xl font-black text-primary-300">{p.n}</span>
                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-900">{p.t}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-xs font-black tracking-[0.2em] text-accent-600">
                <span className="inline-block h-px w-6 bg-accent-500"></span>VOICES
              </p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-3xl">读者怎么看我们</h2>
            </div>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {quotes.map((q) => (
              <figure key={q.name} className="rounded-xl border border-background-200 bg-background-50 p-6">
                <i className="ri-message-3-line text-2xl text-primary-300"></i>
                <blockquote className="mt-3 text-sm leading-7 text-foreground-700">{q.text}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="grid h-10 w-10 items-center justify-center rounded-full bg-secondary-200 text-sm font-black text-secondary-900">
                    {q.name.slice(0, 1)}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{q.name}</p>
                    <p className="text-xs text-foreground-500">{q.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <section id="contact" className="w-full bg-primary-600 text-background-50">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <div className="grid gap-8 md:grid-cols-2 md:items-center">
              <div>
                <p className="text-xs font-black tracking-[0.2em] text-primary-100">GET IN TOUCH</p>
                <h2 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-3xl">投稿、爆料与合作</h2>
                <p className="mt-4 max-w-xl text-sm leading-7 text-primary-100">
                  你有靠谱的线索、想聊聊转载授权，或对某篇内容有异议，都欢迎来信。工作日一般会在 1–2 天内回复，涉及事实更正会优先处理。
                </p>
              </div>
              <div className="rounded-2xl bg-background-50 p-6 text-foreground-800">
                <div className="flex items-center gap-4 rounded-xl bg-background-100 p-4">
                  <span className="grid h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xl text-background-50">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">官方邮箱</p>
                    <a href="mailto:hello@chiguadaily.example" className="text-sm font-bold text-primary-700 hover:underline">
                      hello@chiguadaily.example
                    </a>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {['微博', '公众号', '小红书', 'RSS'].map((s) => (
                    <span key={s} className="rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm font-bold text-foreground-600">
                      吃瓜日报 · {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}